import useTranslation from '@app/hooks/use-translation';

export type ChartOptionDate =
  | 'today'
  | 'currentWeek'
  | 'currentMonth'
  | 'day'
  | 'week'
  | 'month'
  | 'customDate'
  | 'all'
  | 'prevDay'
  | 'prevWeek'
  | 'prevMonth'
  | 'prevQuarter'
  | 'currentQuarter';

export type ReportTimeRange =
  | {
      type: 'l' | 'n' | 'cd' | 'cw' | 'cm' | 'ld' | 'lw' | 'lm' | 'lq' | 'cq';
      parameters?: [{ num: number }];
    }
  | {
      type: 'r';
      parameters?: { condition: '<' | '>'; value: string }[];
    };
/***
 * useChartDateIntervals
 * @return dateIntervals, daysSelectOptions
 */

const useChartDateIntervals = () => {
  const { t } = useTranslation('pages.chartPage');
  const dateKeys: Record<ChartOptionDate, string> = {
    day: 'day',
    week: 'week',
    month: 'month',
    today: 'today',
    currentWeek: 'currentWeek',
    currentMonth: 'currentMonth',
    all: 'all',
    customDate: 'customDate',
    prevDay: 'prevDay',
    prevWeek: 'prevWeek',
    prevMonth: 'prevMonth',
    prevQuarter: 'prevQuarter',
    currentQuarter: 'currentQuarter',
  };
  //
  const daysSelectOptions: Array<{ title: string; key: string; value: string }> = [
    { title: t('select.day'), key: dateKeys.day, value: dateKeys.day },
    { title: t('select.weekdays'), key: dateKeys.week, value: dateKeys.week },
    { title: t('select.monthDays'), key: dateKeys.month, value: dateKeys.month },
    { title: t('select.today'), key: dateKeys.today, value: dateKeys.today },
    { title: t('select.week'), key: dateKeys.currentWeek, value: dateKeys.currentWeek },
    { title: t('select.month'), key: dateKeys.currentMonth, value: dateKeys.currentMonth },
    { title: t('select.prevDay'), key: dateKeys.prevDay, value: dateKeys.prevDay },
    { title: t('select.prevWeek'), key: dateKeys.prevWeek, value: dateKeys.prevWeek },
    { title: t('select.prevMonth'), key: dateKeys.prevMonth, value: dateKeys.prevMonth },
    { title: t('select.prevQuarter'), key: dateKeys.prevQuarter, value: dateKeys.prevQuarter },
    {
      title: t('select.currentQuarter'),
      key: dateKeys.currentQuarter,
      value: dateKeys.currentQuarter,
    },
    { title: t('select.all_data'), key: dateKeys.all, value: dateKeys.all },
    { title: t('select.custom_date'), key: dateKeys.customDate, value: dateKeys.customDate },
  ];

  const intervalTimeBuilder = (fistDate: string | Date | null, lastDate: string | Date | null) => ({
    type: 'r',
    parameters: [
      {
        value: fistDate,
        condition: '>',
      },
      {
        value: lastDate,
        condition: '<',
      },
    ],
  });

  // 'ld' | 'lw' | 'lm' | 'lq' | 'cq';
  const dateIntervals: Record<Exclude<'customDate', ChartOptionDate>, ReportTimeRange> = {
    prevDay: { type: 'ld', parameters: [] },
    prevWeek: { type: 'lw', parameters: [] },
    prevMonth: { type: 'lm', parameters: [] },
    prevQuarter: { type: 'lq', parameters: [] },
    currentQuarter: { type: 'cq', parameters: [] },
    today: { type: 'cd', parameters: [] },
    currentWeek: { type: 'cw', parameters: [] },
    currentMonth: { type: 'cm', parameters: [] },
    day: { type: 'l', parameters: [{ num: 1 }] },
    week: { type: 'l', parameters: [{ num: 7 }] },
    month: { type: 'l', parameters: [{ num: 30 }] },
    all: { type: 'n', parameters: [] },
  };
  const intervalKeysMapNewChartSettingsFormat:
    | Record<
        'current' | 'prev',
        Record<'1' | '7' | '30' | 'quarter', Exclude<ChartOptionDate, 'customDate'>>
      >
    | Record<'last', Record<'1' | '7' | '30', Exclude<ChartOptionDate, 'customDate'>>> = {
    last: {
      '1': 'day',
      '7': 'week',
      '30': 'month',
    },
    current: {
      '1': 'today',
      '7': 'currentWeek',
      '30': 'currentMonth',
      quarter: 'currentQuarter',
    },

    prev: {
      '1': 'prevDay',
      '7': 'prevWeek',
      '30': 'prevMonth',
      quarter: 'prevQuarter',
    },
  } as const;
  const intervalKeysMapNewChartSettingsFormatReverse: Record<
    Exclude<ChartOptionDate, 'customDate'>,
    {
      first: 'last' | 'current' | 'prev' | 'all';
      second: '1' | '7' | '30' | 'quarter' | undefined;
    }
  > = {
    day: { first: 'last', second: '1' },
    week: { first: 'last', second: '7' },
    month: { first: 'last', second: '30' },
    today: { first: 'current', second: '1' },
    currentWeek: { first: 'current', second: '7' },
    currentMonth: { first: 'current', second: '30' },
    prevDay: { first: 'prev', second: '1' },
    prevWeek: { first: 'prev', second: '7' },
    prevMonth: { first: 'prev', second: '30' },
    all: { first: 'all', second: undefined },
    prevQuarter: { first: 'prev', second: 'quarter' },
    currentQuarter: { first: 'current', second: 'quarter' },
  } as const;

  const reversedDateIntervals: Record<string, string> = {
    ld: 'prevDay',
    lw: 'prevWeek',
    lm: 'prevMonth',
    lq: 'prevQuarter',
    cq: 'currentQuarter',
    cd: 'today',
    cw: 'currentWeek',
    cm: 'currentMonth',
    l1: 'day',
    l7: 'week',
    l30: 'month',
    n: 'all',
    r: 'customDate',
  };

  const dateToString = (chartOption?: number | string | ReportTimeRange | undefined) => {
    let currentInterval = dateKeys.customDate;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (chartOption?.type === dateKeys.customDate) return dateKeys.customDate;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore

    if (!chartOption?.type) return 'week';
    Object.keys(dateIntervals).find((key) => {
      if (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        chartOption.type === dateIntervals[key]?.type &&
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        chartOption?.parameters?.[0]?.num === dateIntervals[key].parameters[0]?.num
      ) {
        currentInterval = key;
      }
    });
    return currentInterval;
  };

  const dateToStringNewFormat = (
    chartOption?: number | string | ReportTimeRange | undefined,
  ): {
    first: 'last' | 'current' | 'prev' | 'customDate' | 'all';
    second: '1' | '7' | '30' | 'quarter' | undefined;
  } => {
    let currentInterval = dateKeys.customDate;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (chartOption?.type === 'r') return { first: 'customDate', second: undefined };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore

    if (!chartOption?.type) return 'week';
    Object.keys(dateIntervals).find((key) => {
      if (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        chartOption.type === dateIntervals[key]?.type &&
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        chartOption?.parameters?.[0]?.num === dateIntervals[key].parameters[0]?.num
      ) {
        currentInterval = key;
      }
    });
    return intervalKeysMapNewChartSettingsFormatReverse[currentInterval];
  };

  return {
    dateKeys,
    dateIntervals,
    daysSelectOptions,
    dateToString,
    intervalTimeBuilder,
    reversedDateIntervals,
    intervalKeysMapNewChartSettingsFormat,
    dateToStringNewFormat,
  };
};

export default useChartDateIntervals;
